<template>
    <div class="flex w-full flex-col gap-6">
        <!-- Filtros + PDF: renderizados na toolbar única do UserAudit.
             Sem o alvo, rendem inline no topo. -->
        <Teleport
            v-if="mounted"
            to="#audit-toolbar"
            :disabled="!toolbarTargetExists"
        >
            <div class="flex min-w-0 flex-wrap items-center gap-2">
                <div
                    role="group"
                    aria-label="Selecionar período"
                    class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800"
                >
                    <button
                        v-for="option in presets"
                        :key="option.key"
                        type="button"
                        :aria-pressed="preset === option.key"
                        @click="applyPreset(option.key)"
                        :class="[
                            'rounded-md px-2.5 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                            preset === option.key
                                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                        ]"
                    >
                        {{ option.label }}
                    </button>
                </div>

                <div
                    v-if="preset === 'custom'"
                    class="flex items-center gap-1.5 text-xs"
                >
                    <input
                        type="date"
                        v-model="startDate"
                        :max="endDate"
                        @change="onCustomDate"
                        aria-label="Data inicial"
                        :class="field"
                    />
                    <span class="text-gray-400">até</span>
                    <input
                        type="date"
                        v-model="endDate"
                        :min="startDate"
                        :max="today"
                        @change="onCustomDate"
                        aria-label="Data final"
                        :class="field"
                    />
                </div>

                <select
                    v-model="productId"
                    @change="onProductChange"
                    aria-label="Produto"
                    :class="[field, 'max-w-[12rem]']"
                >
                    <option value="">Todos os produtos</option>
                    <option
                        v-for="product in products"
                        :key="product.id"
                        :value="String(product.id)"
                    >
                        {{ product.name }}
                    </option>
                </select>

                <select
                    v-model="offerId"
                    @change="fetch"
                    :disabled="!productId"
                    aria-label="Oferta"
                    :class="[field, 'max-w-[12rem] disabled:opacity-50']"
                >
                    <option value="">Todas as ofertas</option>
                    <option
                        v-for="offer in offers"
                        :key="offer.id"
                        :value="String(offer.id)"
                    >
                        {{ offer.name }}
                    </option>
                </select>

                <select
                    v-model="paymentMethod"
                    @change="fetch"
                    aria-label="Meio de pagamento"
                    :class="field"
                >
                    <option
                        v-for="method in methods"
                        :key="method.key"
                        :value="method.key"
                    >
                        {{ method.label }}
                    </option>
                </select>

                <button
                    type="button"
                    :disabled="pdf.generating || !report"
                    @click="generatePdf"
                    class="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-gray-300 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:text-white"
                >
                    <ArrowPathIcon
                        v-if="pdf.generating"
                        class="h-4 w-4 animate-spin motion-reduce:animate-none"
                    />
                    <ArrowDownTrayIcon v-else class="h-4 w-4" />
                    {{ pdf.generating ? "Gerando PDF…" : "Gerar PDF" }}
                </button>
            </div>
        </Teleport>

        <p
            v-if="pdf.error"
            role="alert"
            class="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
        >
            {{ pdf.error }}
        </p>

        <CardApprovalLoader
            v-if="run.status !== 'done'"
            :key="run.startedAt"
            :step="run.step"
            :status="run.status"
            :started-at="run.startedAt"
            :filters-label="filtersLabel"
            :error="error ?? ''"
            @retry="fetch"
            @cancel="cancelRun"
        />

        <div
            v-else-if="report"
            :class="[
                'flex flex-col gap-14 transition-opacity lg:gap-[72px]',
                loading ? 'opacity-50' : '',
            ]"
            :aria-busy="loading"
        >
            <header class="flex flex-col gap-4">
                <h1
                    class="text-[2.4rem] font-light leading-[1.02] tracking-[-0.04em] text-gray-900 sm:text-5xl lg:text-[4rem] dark:text-white"
                >
                    {{ questions.title }}<br />
                    <span class="text-[#C4005F] dark:text-[#FF2689]">{{
                        questions.accent
                    }}</span>
                </h1>
                <p
                    class="max-w-3xl text-[15px] leading-relaxed text-gray-600 dark:text-[#D9D9D9]"
                >
                    Tentativas de cartão de crédito da 1ª compra de
                    {{ formatDate(report.period.start) }} a
                    {{ formatDate(report.period.end) }}.
                    <template v-if="report.highlights.buyers"
                        >A conversão conta pessoas, inclusive quem pagou depois
                        por outro meio.</template
                    >
                    A taxa de aprovação conta só as recusas que
                    <b class="font-semibold text-gray-900 dark:text-white"
                        >podiam ter sido aprovadas</b
                    >.
                </p>
                <div
                    class="mt-2 h-0.5 w-60 bg-gradient-to-r from-[#FF9C2B] via-[#FF2689] to-[#9747FF]"
                ></div>
            </header>

            <p
                v-if="!hasAttempts"
                class="rounded-3xl border border-dashed border-gray-300 px-5 py-12 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400"
            >
                Nenhuma tentativa de cartão neste período. Escolha um período
                maior.
            </p>

            <template v-else>
                <section v-if="cards.length" class="flex flex-col gap-5">
                    <div>
                        <p :class="lab">
                            <span :class="labNumber">{{ steps.approval }}</span>
                            · {{ questions.label }}
                        </p>
                        <h2 :class="heading">
                            {{ questions.heading }}
                            <span class="text-[#C4005F] dark:text-[#FF2689]">{{
                                questions.headingAccent
                            }}</span>
                        </h2>
                    </div>

                    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <article
                            v-for="card in cards"
                            :key="card.key"
                            :class="[
                                'relative overflow-hidden rounded-3xl border border-gray-200 bg-gray-50 p-7 dark:border-white/[.08] dark:bg-[#1E1D20]',
                            ]"
                        >
                            <span
                                :class="[
                                    'absolute inset-y-0 left-0 w-1',
                                    card.accent,
                                ]"
                                aria-hidden="true"
                            ></span>
                            <p
                                :class="[
                                    'font-mono text-[11px] font-bold uppercase tracking-[0.08em]',
                                    card.tagColor,
                                ]"
                            >
                                {{ card.tag }}
                            </p>
                            <p
                                class="mt-2 text-2xl font-bold leading-tight tracking-tight text-gray-900 dark:text-white"
                            >
                                {{ card.ask }}
                            </p>
                            <p
                                :class="[
                                    'mt-3 text-5xl font-bold tabular-nums tracking-tight',
                                    card.bigColor,
                                ]"
                            >
                                {{ formatRate(card.rate) }}
                            </p>
                            <p
                                class="mt-1 text-xs text-gray-500 dark:text-gray-400"
                            >
                                {{ card.caption }}
                            </p>
                            <p
                                class="mt-4 rounded-xl border border-gray-200 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-gray-900 dark:border-white/10 dark:bg-black dark:text-white"
                            >
                                {{ card.numerator }}<br />÷ {{ card.formula }}
                            </p>
                            <dl
                                class="mt-5 grid grid-cols-[110px_1fr] gap-x-4 gap-y-2.5 text-[13.5px] leading-snug"
                            >
                                <template
                                    v-for="row in card.rows"
                                    :key="row.term"
                                >
                                    <dt
                                        class="pt-0.5 font-mono text-[11px] uppercase tracking-[0.06em] text-gray-500 dark:text-[#6E6E6E]"
                                    >
                                        {{ row.term }}
                                    </dt>
                                    <dd
                                        class="text-gray-700 dark:text-gray-200"
                                    >
                                        {{ row.text }}
                                    </dd>
                                </template>
                            </dl>
                        </article>
                    </div>
                </section>

                <section class="flex flex-col gap-5">
                    <div>
                        <p :class="lab">
                            <span :class="labNumber">{{ steps.flow }}</span>
                            · O caminho de uma tentativa
                        </p>
                        <h2 :class="heading">
                            Cada tentativa passa por três portas.
                            <span class="text-[#C4005F] dark:text-[#FF2689]"
                                >Cada porta decide em qual conta ela
                                entra.</span
                            >
                        </h2>
                    </div>

                    <div
                        class="rounded-3xl border border-gray-200 bg-gray-50 p-5 sm:p-7 dark:border-white/[.08] dark:bg-[#1E1D20]"
                    >
                        <div
                            class="grid grid-cols-1 items-center gap-3 lg:grid-cols-[1fr_2rem_1fr_2rem_1fr_2rem_1fr] lg:gap-x-0 lg:gap-y-2"
                        >
                            <div
                                :class="[
                                    box,
                                    'border-gray-400 dark:border-gray-500',
                                ]"
                            >
                                <p :class="boxTitle">Tentativa de cartão</p>
                                <p :class="boxNumber">
                                    {{ formatInt(rawRows) }}
                                </p>
                                <p :class="boxText">registros no período</p>
                            </div>
                            <span :class="arrowRight">→</span>
                            <div
                                :class="[
                                    box,
                                    'border-gray-400 dark:border-gray-500',
                                ]"
                            >
                                <p :class="boxTitle">Contar uma vez</p>
                                <p :class="boxText">
                                    repetição em 2 s = 1{{
                                        countSuffix(
                                            report.audit.collapsed_repetitions,
                                        )
                                    }}<br />segundo cartão = 1 pedido{{
                                        countSuffix(
                                            report.audit.collapsed_second_card,
                                        )
                                    }}
                                </p>
                            </div>
                            <span :class="arrowRight">→</span>
                            <div :class="[box, 'border-[#FF9C2B]']">
                                <p :class="boxTitle">É intenção de compra?</p>
                                <p :class="boxText">
                                    pessoa real querendo comprar
                                </p>
                            </div>
                            <span :class="[arrowRight, { invisible: !ignored }]"
                                ><small class="block text-[10px]">não</small
                                >→</span
                            >
                            <div
                                :class="[
                                    box,
                                    'border-gray-400 bg-gray-100 dark:border-gray-500 dark:bg-black',
                                    { invisible: !ignored },
                                ]"
                            >
                                <p :class="boxTitle">Ignorada</p>
                                <p :class="boxNumber">
                                    {{ formatInt(ignored) }}
                                </p>
                                <p :class="boxText">
                                    teste de cartão barrado pelo antifraude
                                </p>
                            </div>

                            <div
                                class="hidden text-sm text-gray-600 lg:col-span-3 lg:row-span-3 lg:block lg:self-start lg:pr-10 lg:pt-4 dark:text-gray-300"
                            >
                                <p>
                                    <b class="text-gray-900 dark:text-white"
                                        >A taxa de aprovação</b
                                    >
                                    só conta o que passa pelas três portas e
                                    chega à decisão do banco sobre uma compra
                                    possível.
                                </p>
                            </div>
                            <span class="hidden lg:block"></span>
                            <span :class="arrowDown"
                                ><small class="text-[10px]">sim</small> ↓</span
                            >
                            <span class="hidden lg:col-span-2 lg:block"></span>

                            <span class="hidden lg:block"></span>
                            <div :class="[box, 'border-[#9747FF]']">
                                <p :class="boxTitle">O banco podia aprovar?</p>
                                <p :class="boxText">
                                    cartão válido, com limite, com a função
                                    pedida
                                </p>
                            </div>
                            <span :class="arrowRight"
                                ><small class="block text-[10px]">não</small
                                >→</span
                            >
                            <div
                                :class="[
                                    box,
                                    'border-gray-400 dark:border-gray-500',
                                ]"
                            >
                                <p :class="boxTitle">Nunca aprovaria</p>
                                <p :class="boxNumber">
                                    {{ formatInt(report.audit.never_approves) }}
                                </p>
                                <p :class="boxText">
                                    saldo, cartão, dado errado, sem a função,
                                    3DS pendente
                                </p>
                            </div>

                            <span class="hidden lg:block"></span>
                            <span :class="arrowDown"
                                ><small class="text-[10px]">sim</small> ↓</span
                            >
                            <span class="hidden lg:col-span-2 lg:block"></span>

                            <span class="hidden lg:col-span-4 lg:block"></span>
                            <div :class="[box, 'border-[#9747FF]']">
                                <p :class="boxTitle">O banco aprovou?</p>
                                <p :class="boxText">resposta do emissor</p>
                            </div>
                            <span
                                :class="[
                                    arrowRight,
                                    'text-emerald-600 dark:text-emerald-400',
                                ]"
                                ><small class="block text-[10px]">sim</small
                                >→</span
                            >
                            <div
                                :class="[
                                    box,
                                    'border-emerald-600 dark:border-emerald-400',
                                ]"
                            >
                                <p :class="boxTitle">Aprovada</p>
                                <p
                                    :class="[
                                        boxNumber,
                                        'text-emerald-700 dark:text-emerald-400',
                                    ]"
                                >
                                    {{ formatInt(report.approval.approved) }}
                                </p>
                            </div>

                            <template v-if="report.highlights.approval">
                                <span
                                    class="hidden lg:col-span-4 lg:block"
                                ></span>
                                <span
                                    :class="[
                                        arrowDown,
                                        'text-[#5B2BB0] dark:text-[#9747FF]',
                                    ]"
                                    ><small class="text-[10px]">não</small>
                                    ↓</span
                                >
                                <span
                                    class="hidden lg:col-span-2 lg:block"
                                ></span>
                            </template>

                            <div
                                v-if="report.highlights.approval"
                                class="flex flex-col gap-3 rounded-2xl border-2 border-dashed border-[#9747FF] bg-violet-50 p-4 sm:flex-row sm:items-center sm:justify-between lg:col-span-7 dark:bg-[#9747FF]/10"
                            >
                                <div>
                                    <p
                                        class="text-xs font-bold text-[#5B2BB0] dark:text-[#B88BFF]"
                                    >
                                        Taxa de aprovação · "quando dava para
                                        aprovar, aprovou?"
                                    </p>
                                    <p
                                        class="mt-2 font-mono text-xs leading-relaxed text-gray-900 dark:text-white"
                                    >
                                        aprovadas ÷ (aprovadas + recusas que
                                        podiam aprovar)<br />
                                        {{
                                            formatInt(report.approval.approved)
                                        }}
                                        ÷ ({{
                                            formatInt(report.approval.approved)
                                        }}
                                        +
                                        {{
                                            formatInt(
                                                report.approval
                                                    .could_approve_refusals,
                                            )
                                        }})
                                    </p>
                                </div>
                                <p
                                    class="text-4xl font-bold tabular-nums tracking-tight text-[#5B2BB0] dark:text-[#B88BFF]"
                                >
                                    {{ formatRate(report.approval.rate) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="flex flex-col gap-5">
                    <div>
                        <p :class="lab">
                            <span :class="labNumber">{{ steps.reasons }}</span>
                            · Motivos de recusa
                        </p>
                        <h2 :class="heading">
                            Cada motivo de recusa,
                            <span class="text-[#C4005F] dark:text-[#FF2689]"
                                >em qual conta entra.</span
                            >
                        </h2>
                    </div>

                    <div class="grid grid-cols-1 items-start gap-4">
                        <article
                            v-for="group in reasonGroups"
                            :key="group.key"
                            class="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-white/[.08] dark:bg-[#1E1D20]"
                        >
                            <header
                                class="flex items-baseline justify-between gap-3 border-b border-gray-200 bg-gray-100 px-[14px] py-3 dark:border-white/[.08] dark:bg-[#353539]"
                            >
                                <div>
                                    <h3
                                        class="text-sm font-bold text-gray-900 dark:text-white"
                                    >
                                        {{ group.title }}
                                    </h3>
                                    <p
                                        class="text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        {{ group.hint }}
                                    </p>
                                </div>
                                <span
                                    :class="[
                                        'shrink-0 rounded-full border px-2.5 py-0.5 font-mono text-[11px]',
                                        group.pill,
                                    ]"
                                    >{{ group.pillLabel }}</span
                                >
                            </header>
                            <ul
                                v-if="group.rows.length"
                                class="divide-y divide-gray-100 dark:divide-white/5"
                            >
                                <li
                                    v-for="reason in group.rows"
                                    :key="reason.label"
                                    class="grid grid-cols-[1fr_auto_4rem] items-center gap-3 px-[14px] py-[11px] text-[13.5px]"
                                >
                                    <div class="min-w-0">
                                        <p
                                            class="text-gray-700 dark:text-gray-200"
                                        >
                                            {{ reason.label }}
                                        </p>
                                        <div
                                            class="mt-1 h-1 rounded-full bg-gray-100 dark:bg-white/5"
                                        >
                                            <div
                                                :class="[
                                                    'h-1 rounded-full',
                                                    group.bar,
                                                ]"
                                                :style="{
                                                    width: `${reason.share ?? 0}%`,
                                                }"
                                            ></div>
                                        </div>
                                    </div>
                                    <span
                                        class="tabular-nums font-semibold text-gray-900 dark:text-white"
                                        >{{ formatInt(reason.count) }}</span
                                    >
                                    <span
                                        class="text-right font-mono text-xs tabular-nums text-gray-500 dark:text-gray-400"
                                        >{{ formatRate(reason.share) }}</span
                                    >
                                </li>
                            </ul>
                            <p
                                v-else
                                class="px-5 py-4 text-sm text-gray-400 dark:text-gray-500"
                            >
                                Nenhuma recusa deste grupo no período.
                            </p>
                        </article>
                    </div>
                    <p class="text-sm text-gray-600 dark:text-gray-300">
                        São respostas dos bancos emissores, comuns em qualquer
                        checkout. Se algum comprador relatou erro na tela, nos
                        envie um print que verificamos juntos.
                    </p>
                </section>

                <section class="flex flex-col gap-5">
                    <div>
                        <p :class="lab">
                            <span :class="labNumber">{{ steps.cases }}</span>
                            · Exemplos
                        </p>
                        <h2 :class="heading">
                            O mesmo dia no checkout,
                            <span class="text-[#C4005F] dark:text-[#FF2689]"
                                >lido pelas duas contas.</span
                            >
                        </h2>
                        <p
                            class="mt-3 text-[15px] leading-relaxed text-gray-600 dark:text-[#D9D9D9]"
                        >
                            Personagens inventados, situações reais.
                        </p>
                    </div>

                    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <article
                            v-for="example in checkoutCases"
                            :key="example.key"
                            :class="[card, 'flex flex-col gap-4 p-6']"
                        >
                            <h3
                                class="text-base font-bold text-gray-900 dark:text-white"
                            >
                                {{ example.title }}
                            </h3>
                            <ol
                                class="flex list-decimal flex-col gap-1 pl-5 text-[13.5px] text-gray-700 dark:text-gray-200"
                            >
                                <li v-for="line in example.story" :key="line">
                                    {{ line }}
                                </li>
                            </ol>
                            <dl class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div
                                    class="rounded-xl bg-white px-4 py-3 dark:bg-black/30"
                                >
                                    <dt :class="lab">Conversão</dt>
                                    <dd
                                        class="mt-1 text-sm font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{ example.conversion }}
                                    </dd>
                                    <dd
                                        class="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        {{ example.conversionNote }}
                                    </dd>
                                </div>
                                <div
                                    class="rounded-xl bg-white px-4 py-3 dark:bg-black/30"
                                >
                                    <dt :class="lab">Aprovação</dt>
                                    <dd
                                        class="mt-1 text-sm font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{ example.approval }}
                                    </dd>
                                    <dd
                                        class="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        {{ example.approvalNote }}
                                    </dd>
                                </div>
                            </dl>
                        </article>
                    </div>
                </section>
            </template>

            <div class="flex flex-col gap-4">
                <div class="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                    <section v-if="goodNumbers.length" :class="[card, 'p-6']">
                        <h2 :class="sectionTitle">
                            <span :class="step">{{ steps.good }}</span>
                            {{ goodNumbersTitle }}
                        </h2>
                        <dl class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div
                                v-for="number in goodNumbers"
                                :key="number.key"
                                class="rounded-2xl bg-violet-50 px-[22px] py-5 dark:bg-[#9747FF]/10"
                            >
                                <dt
                                    class="font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-[#5B2BB0] dark:text-[#B88BFF]"
                                >
                                    {{ number.label }}
                                </dt>
                                <dd
                                    class="mt-2 text-5xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white"
                                >
                                    {{ formatRate(number.rate) }}
                                </dd>
                                <dd
                                    class="mt-0.5 text-xs text-gray-500 dark:text-gray-400"
                                >
                                    {{ number.caption }}
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <section
                        v-if="refusals.by_product.length"
                        :class="[card, 'p-6']"
                    >
                        <h2 :class="sectionTitle">
                            <span :class="step">{{ steps.products }}</span>
                            Onde as recusas aconteceram
                        </h2>
                        <ul class="mt-4 flex flex-col gap-3">
                            <li
                                v-for="item in refusals.by_product"
                                :key="item.product"
                                class="grid grid-cols-[minmax(0,1fr)_minmax(0,9rem)_2.5rem] items-center gap-3 text-sm"
                            >
                                <span
                                    class="truncate text-gray-700 dark:text-gray-200"
                                    >{{ item.product }}</span
                                >
                                <span
                                    class="h-3 rounded-full bg-violet-100 dark:bg-white/5"
                                >
                                    <span
                                        class="block h-3 rounded-full bg-gradient-to-r from-[#6D28D9] to-[#E6338A]"
                                        :style="{
                                            width: `${Math.max(6, (item.refusals / maxProductRefusals) * 100)}%`,
                                        }"
                                    ></span>
                                </span>
                                <span
                                    class="text-right font-semibold tabular-nums text-gray-900 dark:text-white"
                                    >{{ formatShare(item.refusals) }}</span
                                >
                            </li>
                        </ul>
                    </section>
                </div>

                <section v-if="pathRows.length" :class="[card, 'p-6']">
                    <h2 :class="sectionTitle">
                        <span :class="step">{{ steps.paths }}</span>
                        Como os compradores voltaram
                    </h2>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                        {{ formatRate(conversion.recovery.rate) }} das intenções
                        recusadas viraram venda,
                        {{ formatMoney(conversion.recovery.value) }} no preço da
                        oferta.
                        <template
                            v-if="conversion.recovery.within_one_day !== null"
                            >{{
                                formatRate(conversion.recovery.within_one_day)
                            }}
                            voltaram em até 1 dia.</template
                        >
                    </p>
                    <ul class="mt-4 flex flex-col gap-3">
                        <li
                            v-for="path in pathRows"
                            :key="path.key"
                            class="grid grid-cols-[minmax(0,1fr)_minmax(0,9rem)_3rem] items-center gap-3 text-sm"
                        >
                            <span class="text-gray-700 dark:text-gray-200">{{
                                path.label
                            }}</span>
                            <span
                                class="h-3 rounded-full bg-emerald-50 dark:bg-white/5"
                            >
                                <span
                                    class="block h-3 rounded-full bg-emerald-500"
                                    :style="{
                                        width: `${Math.max(6, path.share ?? 0)}%`,
                                    }"
                                ></span>
                            </span>
                            <span
                                class="text-right font-semibold tabular-nums text-gray-900 dark:text-white"
                                >{{ formatInt(path.count) }}</span
                            >
                        </li>
                    </ul>
                </section>

                <div
                    v-if="refusals.tips.length"
                    class="grid grid-cols-1 items-start gap-4 lg:grid-cols-2"
                >
                    <section v-if="refusals.tips.length" :class="[card, 'p-6']">
                        <h2 :class="sectionTitle">
                            <span :class="step">{{ steps.tips }}</span>
                            Como melhorar ainda mais
                        </h2>
                        <ul
                            class="mt-4 flex list-disc flex-col gap-2 pl-5 text-sm text-gray-700 dark:text-gray-200"
                        >
                            <li v-for="tip in refusals.tips" :key="tip.title">
                                <b class="text-gray-900 dark:text-white"
                                    >{{ tip.title }}:</b
                                >
                                {{ tip.text }}
                            </li>
                        </ul>
                    </section>
                </div>
            </div>

            <footer
                class="flex flex-col gap-1.5 border-t border-gray-200 pt-6 text-xs leading-relaxed text-gray-500 dark:border-white/[.08] dark:text-[#6E6E6E]"
            >
                <p v-if="report.audit.card_testing">
                    <b class="text-gray-700 dark:text-gray-200"
                        >{{ formatInt(report.audit.card_testing) }} registros de
                        teste de cartão</b
                    >
                    foram barrados pelo antifraude e ficaram fora dos
                    indicadores.
                </p>
                <p>
                    <b class="text-gray-700 dark:text-gray-200"
                        >{{ formatInt(report.audit.never_approves) }} recusas
                        nunca aprovariam</b
                    >
                    (saldo, cartão bloqueado ou vencido, dado errado, cartão sem
                    a função, autenticação não concluída): contam na conversão,
                    não na aprovação.
                </p>
                <p>
                    Cada comprador aparece uma vez nas recusas. Quem teve recusa
                    e comprou em seguida não conta como recusa nas taxas.
                </p>
                <p>Renovações de assinatura ficam fora.</p>
            </footer>
        </div>
    </div>
</template>

<script>
import { ArrowDownTrayIcon, ArrowPathIcon } from "@heroicons/vue/24/outline";
import CardApprovalLoader from "@/components/user/audit/CardApprovalLoader.vue";
import { getEcho } from "@/lib/echo";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 180000;

const isoDate = (date) => {
    const offset = date.getTimezoneOffset() * 60000;

    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const daysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);

    return isoDate(date);
};

export default {
    components: { ArrowDownTrayIcon, ArrowPathIcon, CardApprovalLoader },
    props: ["user"],

    data() {
        return {
            presets: [
                { key: "7", label: "7 dias" },
                { key: "30", label: "30 dias" },
                { key: "90", label: "90 dias" },
                { key: "365", label: "12 meses" },
                { key: "last-month", label: "Mês anterior" },
                { key: "custom", label: "Personalizado" },
            ],
            methods: [
                { key: "card", label: "Cartão" },
                { key: "pix", label: "Pix" },
                { key: "bank_slip", label: "Boleto" },
                { key: "all", label: "Todos os meios" },
            ],
            preset: "30",
            startDate: daysAgo(29),
            endDate: isoDate(new Date()),
            today: isoDate(new Date()),
            products: [],
            productId: "",
            offerId: "",
            paymentMethod: "card",
            report: null,
            loading: false,
            error: null,
            run: {
                stop: null,
                token: 0,
                step: "prepare",
                status: "running",
                startedAt: Date.now(),
            },
            pdf: { generating: false, error: null, stop: null },
            field: "rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200",
            card: "rounded-2xl border border-gray-200 bg-gray-50 dark:border-white/[.08] dark:bg-[#1E1D20]",
            sectionTitle:
                "flex items-baseline font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-[#6E6E6E]",
            step: "text-[#C4005F] after:mx-1.5 after:text-gray-400 after:content-['·'] dark:text-[#FF2689]",
            lab: "font-mono text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-[#6E6E6E]",
            labNumber: "text-[#C4005F] dark:text-[#FF2689]",
            heading:
                "mt-2 text-[1.6rem] font-light leading-tight tracking-[-0.03em] text-gray-900 lg:text-[2.3rem] dark:text-white",
            box: "flex flex-col items-center justify-center self-stretch rounded-2xl border-[1.5px] bg-white px-3 py-3 text-center dark:bg-[#12111A]",
            boxTitle:
                "text-sm font-bold leading-tight text-gray-900 dark:text-white",
            boxNumber:
                "mt-1 text-2xl font-bold tabular-nums tracking-tight text-gray-900 dark:text-white",
            boxText:
                "mt-1 text-xs leading-snug text-gray-500 dark:text-gray-400",
            arrowRight:
                "hidden text-center font-mono text-lg leading-none text-gray-400 lg:block",
            arrowDown: "text-center font-mono text-sm text-gray-400",
            mounted: false,
            toolbarTargetExists: false,
        };
    },

    computed: {
        userId() {
            return this.$route.params.user_id ?? this.user.id;
        },

        filtersLabel() {
            const period =
                this.preset === "custom"
                    ? `${this.formatDate(this.startDate)} a ${this.formatDate(this.endDate)}`
                    : this.presets.find((item) => item.key === this.preset)
                          ?.label;
            const product = this.products.find(
                (item) => String(item.id) === this.productId,
            );
            const offer = this.offers.find(
                (item) => String(item.id) === this.offerId,
            );
            const method = this.methods.find(
                (item) => item.key === this.paymentMethod,
            );

            return [
                period,
                product?.name ?? "Todos os produtos",
                offer?.name ?? "Todas as ofertas",
                method?.label,
            ]
                .filter(Boolean)
                .join(" · ");
        },

        query() {
            const params = {
                start_date: this.startDate,
                end_date: this.endDate,
                payment_method: this.paymentMethod,
            };

            if (this.productId) {
                params.product_id = this.productId;
            }

            if (this.offerId) {
                params.offer_id = this.offerId;
            }

            return new URLSearchParams(params).toString();
        },

        hasAttempts() {
            return (
                this.report.approval.attempts +
                    this.report.audit.never_approves >
                0
            );
        },

        ignored() {
            return this.report.audit.card_testing;
        },

        rawRows() {
            return (
                this.report.gross.approved +
                this.report.gross.refused +
                this.ignored
            );
        },

        cards() {
            const { approval, highlights } = this.report;
            const { buyers } = this.conversion;
            const cards = [];

            if (highlights.buyers) {
                cards.push({
                    key: "conversion",
                    tag: "Conversão",
                    ask: "Quem quis comprar, comprou?",
                    rate: buyers.rate,
                    caption: `${this.formatInt(buyers.converted)} de ${this.formatInt(buyers.people)} pessoas que tentaram comprar`,
                    numerator: "pessoas que compraram",
                    formula: "pessoas que tentaram comprar",
                    accent: "bg-[#C4005F] dark:bg-[#FF2689]",
                    tagColor: "text-[#C4005F] dark:text-[#FF2689]",
                    bigColor: "text-[#C4005F] dark:text-[#FF2689]",
                    rows: [
                        {
                            term: "Unidade",
                            text: "Pessoa (e, por baixo, a intenção de compra: pessoa × oferta)",
                        },
                        {
                            term: "Denominador",
                            text: "Todo mundo que tentou pagar de verdade, inclusive com saldo insuficiente ou cartão bloqueado",
                        },
                        {
                            term: "Numerador",
                            text: "Quem terminou com um pagamento aprovado, na hora ou depois, por qualquer meio",
                        },
                        {
                            term: "Responde por",
                            text: "O funil do produtor e a recuperação da Ticto",
                        },
                    ],
                });
            }

            if (!highlights.approval) {
                return cards;
            }

            return [
                ...cards,
                {
                    key: "approval",
                    tag: "Taxa de aprovação",
                    ask: "Quando dava para aprovar, a Ticto aprovou?",
                    rate: approval.rate,
                    caption: `${this.formatInt(approval.approved)} aprovadas de ${this.formatInt(approval.attempts)} tentativas que podiam aprovar`,
                    numerator: "tentativas aprovadas",
                    formula: "(aprovadas + recusas que podiam aprovar)",
                    accent: "bg-[#5B2BB0] dark:bg-[#9747FF]",
                    tagColor: "text-[#5B2BB0] dark:text-[#B88BFF]",
                    bigColor: "text-[#5B2BB0] dark:text-[#B88BFF]",
                    rows: [
                        {
                            term: "Unidade",
                            text: "Tentativa, depois de contar repetições e segundo cartão uma vez",
                        },
                        {
                            term: "Podia aprovar",
                            text: "Só transação negada, suspeita de fraude e contato com o emissor. Erros ignorados",
                        },
                        {
                            term: "Responde por",
                            text: "A Ticto, o adquirente e a relação com os bancos",
                        },
                    ],
                },
            ];
        },

        questions() {
            const both = this.cards.length > 1;
            const conversionOnly =
                this.cards.length === 1 && this.cards[0].key === "conversion";

            if (both) {
                return {
                    title: "Duas perguntas, dois números.",
                    accent: "Cada número tem um dono.",
                    label: "As duas perguntas",
                    heading: "Conversão mede a pessoa.",
                    headingAccent: "Aprovação mede a Ticto e o banco.",
                };
            }

            if (conversionOnly) {
                return {
                    title: "Uma pergunta, um número.",
                    accent: "Conversão mede a pessoa.",
                    label: "A conversão",
                    heading: "Quem quis comprar, comprou?",
                    headingAccent: "Conta a pessoa, não a tentativa.",
                };
            }

            return {
                title: "Uma pergunta, um número.",
                accent: "Aprovação mede a Ticto e o banco.",
                label: "A taxa de aprovação",
                heading: "Quando dava para aprovar, aprovou?",
                headingAccent: "Só entra o que o banco podia aprovar.",
            };
        },

        checkoutCases() {
            return [
                {
                    key: "ana",
                    title: "Ana · limite curto, voltou no Pix",
                    story: [
                        "Tenta R$ 2.999 em 12x no cartão. Banco: saldo insuficiente.",
                        "Tenta de novo no mesmo cartão. Banco: saldo insuficiente.",
                        "Gera um Pix e paga.",
                    ],
                    conversion: "1 pessoa, comprou.",
                    conversionNote: "Pagou no Pix depois da recusa.",
                    approval: "Nada entra.",
                    approvalNote:
                        "As duas recusas nunca aprovariam. Não pesam a favor nem contra a Ticto.",
                },
                {
                    key: "carla",
                    title: "Carla · digitou o código de segurança errado",
                    story: [
                        "Tenta R$ 299. Banco: código de segurança inválido.",
                        "Fecha a página e não volta.",
                    ],
                    conversion: "1 pessoa, não comprou.",
                    conversionNote:
                        "Fica com o motivo código de segurança digitado errado.",
                    approval: "Nada entra.",
                    approvalNote:
                        "Com o código errado, o banco nunca aprovaria. Não é desempenho da Ticto.",
                },
            ];
        },

        reasonGroups() {
            return [
                {
                    key: "never-approves",
                    title: "Recusas que nunca aprovariam",
                    hint: "O banco não aprovaria estas tentativas como estavam.",
                    bar: "bg-gray-400 dark:bg-gray-500",
                    pill: "border-gray-300 text-gray-500 dark:border-white/10 dark:text-gray-400",
                    pillLabel: "Fica fora",
                    rows: this.report.never_approves_reasons,
                },
            ];
        },

        offers() {
            return (
                this.products.find(
                    (product) => String(product.id) === this.productId,
                )?.offers ?? []
            );
        },

        conversion() {
            return this.report.conversion;
        },

        refusals() {
            return this.report.conversion.refusals;
        },

        goodNumbers() {
            const { highlights } = this.report;
            const { intentions, recovery } = this.conversion;
            const numbers = [];

            if (highlights.intentions && !highlights.buyers) {
                numbers.push({
                    key: "intentions",
                    label: "Intenções de compra pagas",
                    rate: intentions.rate,
                    caption: `${this.formatInt(intentions.approved)} de ${this.formatInt(intentions.total)} intenções`,
                });
            }

            if (highlights.recovery) {
                numbers.push({
                    key: "recovery",
                    label: "Recusas que viraram venda",
                    rate: recovery.rate,
                    caption: this.formatMoney(recovery.value),
                });
            }

            return numbers;
        },

        goodNumbersTitle() {
            return "Os números que importam";
        },

        totalProductRefusals() {
            return this.refusals.by_product.reduce(
                (sum, item) => sum + item.refusals,
                0,
            );
        },

        maxProductRefusals() {
            return Math.max(
                1,
                ...this.refusals.by_product.map((item) => item.refusals),
            );
        },

        steps() {
            const visible = {
                approval: this.hasAttempts && this.cards.length > 0,
                flow: this.hasAttempts,
                reasons: this.hasAttempts,
                cases: this.hasAttempts,
                good: this.goodNumbers.length > 0,
                products: this.refusals.by_product.length > 0,
                paths: this.pathRows.length > 0,
                tips: this.refusals.tips.length > 0,
            };
            let next = 0;

            return Object.fromEntries(
                Object.entries(visible).map(([key, shown]) => [
                    key,
                    shown ? String(++next).padStart(2, "0") : null,
                ]),
            );
        },

        pathRows() {
            if (!this.report.highlights.recovery) {
                return [];
            }

            return this.conversion.recovery.paths.filter((path) => path.count);
        },
    },

    mounted() {
        this.toolbarTargetExists = Boolean(
            document.getElementById("audit-toolbar"),
        );
        this.mounted = true;
        this.loadOptions();
        this.fetch();
    },

    beforeUnmount() {
        this.stopRun();
        this.stopPdf();
    },

    methods: {
        applyPreset(key) {
            this.preset = key;

            if (key === "custom") {
                return;
            }

            this.endDate = this.today;

            if (key === "last-month") {
                const now = new Date();
                this.startDate = isoDate(
                    new Date(now.getFullYear(), now.getMonth() - 1, 1),
                );
                this.endDate = isoDate(
                    new Date(now.getFullYear(), now.getMonth(), 0),
                );
            } else {
                this.startDate = daysAgo(Number(key) - 1);
            }

            this.fetch();
        },

        onCustomDate() {
            if (!this.startDate || !this.endDate) {
                return;
            }

            this.fetch();
        },

        onProductChange() {
            this.offerId = "";
            this.fetch();
        },

        loadOptions() {
            axios
                .get(`/api/user/${this.userId}/card-approval/options`)
                .then(({ data }) => {
                    this.products = data.products;
                })
                .catch(() => {
                    this.products = [];
                });
        },

        fetch() {
            this.stopRun();
            const token = ++this.run.token;
            this.loading = true;
            this.error = null;
            Object.assign(this.run, {
                step: "prepare",
                status: "running",
                startedAt: Date.now(),
            });

            axios
                .post(`/api/user/${this.userId}/card-approval?${this.query}`)
                .then(({ data }) => {
                    if (token !== this.run.token) {
                        return;
                    }

                    this.run.stop = this.watchJob(
                        data.id,
                        `/api/user/${this.userId}/card-approval/${data.id}`,
                        {
                            onStep: (step) => {
                                this.run.step = step;
                            },
                            onReady: (result) => {
                                this.report = result.report;
                                this.loading = false;
                                this.run.status = "done";
                            },
                            onFailed: (message) =>
                                this.failRun(
                                    message ??
                                        "Não foi possível calcular a análise. Tente de novo.",
                                ),
                            timeoutMessage:
                                "A análise está demorando mais que o normal. Tente um período menor.",
                        },
                    );
                })
                .catch((error) => {
                    if (token === this.run.token) {
                        this.failRun(
                            error.response?.data?.errors?.[0] ??
                                "Não foi possível carregar a análise. Tente de novo.",
                        );
                    }
                });
        },

        failRun(message) {
            this.stopRun();
            this.loading = false;
            this.error = message;
            this.run.status = "failed";
        },

        cancelRun() {
            this.stopRun();
            this.run.token++;
            this.loading = false;
            this.run.status = "cancelled";
        },

        stopRun() {
            this.run.stop?.();
            this.run.stop = null;
        },

        /**
         * Acompanha um job da aprovação de cartão pelo canal privado do
         * Pusher: cada etapa chega como evento .progress e, no ready, o
         * resultado vem do endpoint de status. A consulta ao se inscrever
         * pega o job que terminou antes da inscrição. Sem Pusher no
         * ambiente, cai no polling. Devolve a função que para de acompanhar.
         */
        watchJob(id, statusUrl, { onStep, onReady, onFailed, timeoutMessage }) {
            const echo = getEcho();
            const channelName = `card-approval.${id}`;
            let finished = false;
            let pollTimer = null;

            const stop = () => {
                finished = true;
                clearTimeout(pollTimer);
                clearTimeout(deadline);
                echo?.leave(channelName);
            };

            const deadline = setTimeout(() => {
                if (!finished) {
                    stop();
                    onFailed(timeoutMessage);
                }
            }, POLL_TIMEOUT_MS);

            const check = () =>
                axios
                    .get(statusUrl)
                    .then(({ data }) => {
                        if (finished) {
                            return null;
                        }

                        if (data.status === "ready") {
                            stop();
                            onReady(data);
                        } else if (data.status === "failed") {
                            stop();
                            onFailed();
                        }

                        return data.status;
                    })
                    .catch((error) => {
                        if (!finished) {
                            stop();
                            onFailed(error.response?.data?.errors?.[0]);
                        }

                        return null;
                    });

            if (echo === null) {
                const poll = () => {
                    pollTimer = setTimeout(
                        () =>
                            check().then((status) => {
                                if (status === "processing") {
                                    poll();
                                }
                            }),
                        POLL_INTERVAL_MS,
                    );
                };

                poll();

                return stop;
            }

            echo.private(channelName)
                .subscribed(() => check())
                .listen(".progress", (event) => {
                    if (finished) {
                        return;
                    }

                    if (event.status === "processing") {
                        onStep?.(event.step);
                    } else {
                        check();
                    }
                });

            return stop;
        },

        generatePdf() {
            this.stopPdf();
            this.pdf.generating = true;
            this.pdf.error = null;

            axios
                .post(
                    `/api/user/${this.userId}/card-approval/pdf?${this.query}`,
                )
                .then(({ data }) => {
                    this.pdf.stop = this.watchJob(
                        data.id,
                        `/api/user/${this.userId}/card-approval/pdf/${data.id}`,
                        {
                            onReady: (result) => {
                                this.pdf.generating = false;
                                window.location.href = result.url;
                            },
                            onFailed: (message) =>
                                this.failPdf(
                                    message ??
                                        "Não foi possível gerar o PDF. Tente de novo.",
                                ),
                            timeoutMessage:
                                "O PDF está demorando mais que o normal. Tente de novo em alguns minutos.",
                        },
                    );
                })
                .catch((error) =>
                    this.failPdf(
                        error.response?.data?.errors?.[0] ??
                            "Não foi possível pedir o PDF. Tente de novo.",
                    ),
                );
        },

        failPdf(message) {
            this.stopPdf();
            this.pdf.generating = false;
            this.pdf.error = message;
        },

        stopPdf() {
            this.pdf.stop?.();
            this.pdf.stop = null;
        },

        formatRate(value) {
            if (value === null || value === undefined) {
                return "";
            }

            return (
                Number(value).toLocaleString("pt-BR", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                }) + "%"
            );
        },

        formatInt(value) {
            return Number(value ?? 0).toLocaleString("pt-BR");
        },

        formatShare(value) {
            return this.totalProductRefusals
                ? `${Math.round((value / this.totalProductRefusals) * 100)}%`
                : "-";
        },

        countSuffix(value) {
            return value ? ` (${this.formatInt(value)})` : "";
        },

        formatMoney(cents) {
            return (Number(cents ?? 0) / 100).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            });
        },

        formatDate(value) {
            return value.split("-").reverse().join("/");
        },
    },
};
</script>
